/**
 * Team Comms App
 * 
 * Main container for Team Communications features including:
 * - Team Feed (posts, comments, reactions, polls)
 * - Messages (iMessage-style chat via MessagingApp)
 * 
 * Layout:
 * - Desktop: Left sidebar with tabs (Team Feed / Messages) + main content
 * - Mobile: Bottom tab bar for navigation between views
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDesign } from '../../contexts/DesignContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserAvatar } from '@/components/shared';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MessageSquare,
  Users,
  Search,
  X,
  ChevronDown,
  Loader2,
  LogOut,
  Check,
  Settings,
  User,
  Bell,
  Cake,
  CheckSquare,
  Tag,
  Play
} from 'lucide-react';
import { formatTimeAgo } from '../../utils/timeFormat';

// Import MessagingApp for the Messages tab
import { MessagingApp } from '../messaging';

// Import GroupsView for admin group management
import GroupsView from './components/GroupsView';

// Import Team Member Profile Settings
import TeamMemberProfileSettings from '../team/TeamMemberProfileSettings';

// Import extracted components
import { 
  VoiceMemoPlayer, 
  VoiceMemoButton, 
  GifPickerDialog, 
  PostImageGallery, 
  PollDisplay,
  SearchResultsDropdown,
  PostCard,
  FeedView,
  MobileTasksView
} from './components';

// Import shared NotificationPanel
import { NotificationPanel } from '../shared/NotificationPanel';
import { spaNavigate } from '../../lib/spaNavigate';
import { useSharedItems, visibleSharedTasks } from '../planner/hooks/useSharedItems';
// Services (Simple Planner) member view for /team (Phase 5)
import { Music } from 'lucide-react';
import { useServicesMode } from '../services/hooks/useServicesMode';
import ServicesView from './components/ServicesView';
import { useServicesPending } from '../services/hooks/useServicesPending';

// Import shared utilities
import { 
  API_URL, 
  getDayWithSuffix 
} from './utils/constants';

// Import hooks
import { useTeamCommsSearch } from './hooks';

// Import Firestore for conversations
import { useConversations } from '../messaging/hooks/useConversations';
import { useUnreadCounts } from '../messaging/hooks/useUnreadCounts';

// ==================== MAIN COMPONENT ====================

function TeamCommsApp({
  user,
  onBack,
  highlightPostId,
  highlightCommentId: initialHighlightCommentId,
  isStandalone = false,
  globalPosts = [],
  setGlobalPosts,
  globalTasks = [],
  setGlobalTasks,
  setHoveredItem,
  isTeamMemberView = false,
  initialView = 'feed',
  onUpdateUser,
  initialConversationId = null,
  initialMessageId = null,
  notificationHook,
  markMessagesAsRead,
  archivedConversationIds = [],
  // Fix 7 G4: {serviceId, type} from a Services tap outside Team Comms (dashboard
  // bell, push, desktop banner) for a non-admin: open the Services view on it.
  serviceHighlight = null,
  isOnline = true  // Offline mode support
}) {
  const { isStudio } = useDesign();
  const posts = globalPosts;
  const setPosts = setGlobalPosts || (() => {});
  const [loading, setLoading] = useState(globalPosts.length === 0);
  // Surfaces a real "couldn't load — retry" state instead of silently showing
  // an empty feed when the backend is unreachable/erroring, so an outage is
  // visible rather than mistaken for "no posts".
  const [feedError, setFeedError] = useState(false);
  const [highlightedPostId, setHighlightedPostId] = useState(highlightPostId);
  
  // Notification state
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  // Destructure the hook for clean access
  const { notifications: teamCommsNotifications, unreadCount, markRead, markReadByIds, markAllRead, markReadByTarget, setActiveConversation } = notificationHook || {};

  // Bell wiggle: fires once on entering Team Comms so users are drawn to the
  // notification tray, and also every time the unread count increases (matches
  // the Dashboard's `.bell-ring` behavior). Hover wiggle is CSS-only via
  // `.bell-hover-wiggle`.
  const [bellRinging, setBellRinging] = useState(true);
  const prevUnreadRef = useRef(null);
  useEffect(() => {
    // Play the entrance ring once on mount, then clear it.
    const t = setTimeout(() => setBellRinging(false), 900);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = unreadCount || 0;
    if (prev !== null && (unreadCount || 0) > prev) {
      setBellRinging(true);
      const t = setTimeout(() => setBellRinging(false), 900);
      return () => clearTimeout(t);
    }
  }, [unreadCount]);
  
  // Unread DM counts from Firestore (for Messages tab red dot)
  // Exclude archived conversations — user deliberately hid them
  const { unreadCounts } = useUnreadCounts(user?.id);
  const totalUnreadDMs = Object.entries(unreadCounts).reduce((sum, [convId, count]) => {
    if (archivedConversationIds.includes(convId)) return sum;
    return sum + count;
  }, 0);

  // Unread feed notifications (for Feed tab red dot) — same filter pattern as unreadPostIds below
  const hasUnreadFeed = (teamCommsNotifications || []).some(
    n => !n.is_read && n.sender_id !== user?.id && !['dm', 'message_reaction'].includes(n.type)
  );
  
  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // View toggle state: 'feed', 'messages', or 'settings'
  const [currentView, setCurrentView] = useState(initialView);
  
  // Track which conversation is currently being viewed (for "born as read" pattern)
  const [activeConversationId, setActiveConversationId] = useState(null);
  
  // Profile dropdown state (for team member view)
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileDropdownRef = useRef(null);
  
  // Get conversations for message search
  const { conversations } = useConversations(user?.id);
  
  // Unified search hook
  const {
    searchQuery,
    setSearchQuery,
    isSearching,
    isSearchActive,
    feedResults,
    messageResults,
    clearSearch
  } = useTeamCommsSearch(user?.id, conversations, user?.church_id);
  
  // Search UI state
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [highlightedCommentId, setHighlightedCommentId] = useState(initialHighlightCommentId || null);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  
  // State for navigating to a specific message from search
  const [targetMessageData, setTargetMessageData] = useState(null);
  
  // State for notification-driven message highlighting (separate from search)
  const [messageHighlight, setMessageHighlight] = useState(null);

  // Team Comms intro video — admin-only orange "Watch Instructions" button.
  // Same pattern as PlannerApp / OnboardingApp / GroupsView. Persisted on
  // user_profiles.team_comms_intro_dismissed via /api/auth/user-preferences.
  const [showIntroVideo, setShowIntroVideo] = useState(false);
  const [introVideoDismissed, setIntroVideoDismissed] = useState(true);

  useEffect(() => {
    const checkIntroDismissed = async () => {
      if (!user?.id) return;
      try {
        const res = await fetch(`${API_URL}/api/auth/user-preferences/${user.id}`);
        if (res.ok) {
          const data = await res.json();
          setIntroVideoDismissed(data.team_comms_intro_dismissed === true);
        } else {
          setIntroVideoDismissed(false);
        }
      } catch {
        setIntroVideoDismissed(false);
      }
    };
    checkIntroDismissed();
  }, [user?.id]);

  const handleDismissIntro = useCallback(async () => {
    setShowIntroVideo(false);
    setIntroVideoDismissed(true);
    if (!user?.id) return;
    try {
      await fetch(`${API_URL}/api/auth/user-preferences/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_comms_intro_dismissed: true, email: user.email })
      });
    } catch (err) {
      console.error('Error saving Team Comms intro dismissed:', err);
    }
  }, [user?.id, user?.email]);

  // Handle ESC key to close intro video — mirrors PlannerApp/OnboardingApp pattern
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showIntroVideo) {
        handleDismissIntro();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showIntroVideo, handleDismissIntro]);
  
  // Clear the highlight target after it has been consumed (prevents re-highlighting on tab switch)
  const handleHighlightConsumed = useCallback(() => {
    setTargetMessageData(prev => prev ? { ...prev, message: null } : null);
  }, []);
  
  const handleMessageHighlightComplete = useCallback(() => {
    setMessageHighlight(null);
  }, []);
  
  const [birthdaysThisMonth, setBirthdaysThisMonth] = useState([]);
  // Church-level birthday auto-post settings (admin gear on the Birthdays card).
  // audience: 'everyone' (whole church) | 'groups' (only the person's groups).
  const [birthdayPosts, setBirthdayPosts] = useState({ enabled: true, audience: 'groups' });
  const isChurchAdmin = user?.role === 'admin' || user?.role === 'owner';

  // Shared tasks (church-level, Mongo-backed). Non-admin users gain the Tasks
  // tab permanently once they've EVER been assigned a shared task.
  const {
    sharedItems, sharedContext, refetchShared,
    createSharedItem, toggleSharedItem, updateSharedItem, deleteSharedItem,
  } = useSharedItems(user?.id, { focusRefetch: true });
  const mySharedTasks = useMemo(() => visibleSharedTasks(sharedItems), [sharedItems]);
  const canSeeTasksTab = isChurchAdmin || sharedItems.some(i => i.item_type === 'task');
  const { tabVisible: canSeeServicesTab, mode: servicesModeForDot } = useServicesMode(user?.id);
  const [highlightSharedTaskId, setHighlightSharedTaskId] = useState(null);
  const [highlightServiceId, setHighlightServiceId] = useState(null);
  const [highlightServiceType, setHighlightServiceType] = useState(null);
  const [highlightServiceNonce, setHighlightServiceNonce] = useState(0);   // Fix 7c F12: a second tap on the same service re-opens it
  const handledServiceHighlight = useRef(null);

  // Real-time sync: a shared_planner notification arriving over the existing
  // WebSocket means shared data changed — refetch (also covers task view opens).
  const sharedNotifStamp = useMemo(() => {
    const list = (teamCommsNotifications || []).filter(n => n.type === 'shared_planner');
    return list.length ? `${list.length}:${list[0]?.id || ''}` : '';
  }, [teamCommsNotifications]);
  useEffect(() => {
    if (sharedNotifStamp) refetchShared();
  }, [sharedNotifStamp, refetchShared]);
  // Services: a service notification arriving refreshes the Services view (L6-2).
  const servicesNotifStamp = useMemo(() => {
    const list = (teamCommsNotifications || []).filter(n => ['service_request','service_response','service_updated','service_comment'].includes(n.type));
    return list.length ? `${list.length}:${list[0]?.id || ''}` : '';
  }, [teamCommsNotifications]);
  // Fix 7 G4: a Services tap from outside lands like the Team Comms bell's own tap.
  // Fix 7c F2: only when this user can see the Services tab (never for a flag-off
  // church), and once per tap (F12: each tap carries a nonce for the view).
  useEffect(() => {
    if (!serviceHighlight || !canSeeServicesTab || handledServiceHighlight.current === serviceHighlight) return;
    handledServiceHighlight.current = serviceHighlight;
    setCurrentView('services');
    setHighlightServiceId(serviceHighlight.serviceId || null);
    setHighlightServiceType(serviceHighlight.type || null);
    setHighlightServiceNonce((n) => n + 1);
  }, [serviceHighlight, canSeeServicesTab]);
  // Fix 7c F4: a highlight lands once. Leaving the Services view (or its Back, via
  // onHighlightConsumed) clears it, so a later visit opens the list.
  const clearServiceHighlight = useCallback(() => {
    setHighlightServiceId(null);
    setHighlightServiceType(null);
  }, []);
  useEffect(() => {
    if (currentView !== 'services') clearServiceHighlight();
  }, [currentView, clearServiceHighlight]);
  // Fix 7 G3: the Services dot (answers outstanding), read only while the tab is visible.
  const { pendingCount: servicesPendingCount, setPendingCount: setServicesPendingCount } = useServicesPending(user?.id, canSeeServicesTab && servicesModeForDot === 'simple', servicesNotifStamp, currentView === 'services');   // Fix 7c F6: never in pco mode
  useEffect(() => {
    if (currentView === 'tasks') refetchShared();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);

  // R3 (2026-09-03): per-church "leaders only can post" flag, read from the
  // church settings route (additive field) so FeedView can hide the composer.
  // R38 (2026-09-07): the switch itself moved to Settings → Team Management
  // (admin/owner-only page); re-read when the tab regains focus so a change
  // made there shows up here without a reload.
  const [leadersOnlyPosting, setLeadersOnlyPosting] = useState(false);
  const fetchLeadersOnlyPosting = () => {
    if (!user?.church_id) return;
    fetch(`${API_URL}/api/comms/v3/church/timezone/${encodeURIComponent(user.church_id)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setLeadersOnlyPosting(Boolean(d.leaders_only_posting)); })
      .catch(() => {});
  };
  useEffect(() => {
    fetchLeadersOnlyPosting();
    const onVisible = () => { if (!document.hidden) fetchLeadersOnlyPosting(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user?.church_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBirthdayPostsSetting = async () => {
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/church/birthday-posts/${encodeURIComponent(user?.church_id || '')}`);
      if (response.ok) {
        const data = await response.json();
        setBirthdayPosts({ enabled: data.enabled !== false, audience: data.audience === 'everyone' ? 'everyone' : 'groups' });
      }
    } catch (err) {
      console.error('Error fetching birthday posts setting:', err);
    }
  };

  // Optimistic partial update ({ enabled } or { audience }); reverts on failure.
  const saveBirthdayPosts = async (patch) => {
    const previous = birthdayPosts;
    setBirthdayPosts(prev => ({ ...prev, ...patch }));
    try {
      const params = new URLSearchParams({ ...patch, user_id: user?.id || '' });
      const response = await fetch(
        `${API_URL}/api/comms/v3/church/birthday-posts/${encodeURIComponent(user?.church_id || '')}?${params}`,
        { method: 'PUT' }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      toast.success(
        'enabled' in patch
          ? (patch.enabled ? 'Automatic birthday posts on' : 'Automatic birthday posts off')
          : (patch.audience === 'everyone' ? 'Birthday posts will go to everyone' : 'Birthday posts will go to their groups')
      );
    } catch {
      setBirthdayPosts(previous);
      toast.error('Failed to update birthday posts');
    }
  };
  
  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle highlightPostId prop changes
  useEffect(() => {
    if (highlightPostId) {
      setHighlightedPostId(highlightPostId);
      if (markReadByTarget) markReadByTarget({ postId: highlightPostId });
      const timer = setTimeout(() => setHighlightedPostId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightPostId, markReadByTarget]);

  // Handle initialView prop changes (e.g., from URL params)
  useEffect(() => {
    if (initialView && initialView !== currentView) {
      setCurrentView(initialView);
    }
    // Only run when initialView changes, not currentView
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialView]);

  // Handle initial conversation from dashboard/push notification click
  useEffect(() => {
    if (initialConversationId && initialView === 'messages') {
      if (markReadByTarget) markReadByTarget({ conversationId: initialConversationId });
      setMessageHighlight({
        conversationId: initialConversationId,
        messageId: initialMessageId || null
      });
    }
  }, [initialConversationId, initialMessageId, initialView, markReadByTarget]);

  // Handle highlightCommentId prop changes
  useEffect(() => {
    if (initialHighlightCommentId) {
      setHighlightedCommentId(initialHighlightCommentId);
      if (markReadByTarget) markReadByTarget({ commentId: initialHighlightCommentId });
      const timer = setTimeout(() => setHighlightedCommentId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [initialHighlightCommentId, markReadByTarget]);

  // Sync activeConversationId with the notification hook's "born as read" tracking
  useEffect(() => {
    if (setActiveConversation) {
      setActiveConversation(activeConversationId);
    }
    // Leaving Team Comms (or switching conversation) must stop marking that
    // conversation's new messages as already read (final review 2026-09-07, P2-3).
    return () => { if (setActiveConversation) setActiveConversation(null); };
  }, [activeConversationId, setActiveConversation]);

  // Define functions first
  const fetchPosts = async () => {
    try {
      setFeedError(false);
      const churchId = user?.church_id || '';
      const userId = user?.id || '';
      const response = await fetch(`${API_URL}/api/comms/v3/posts?church_id=${encodeURIComponent(churchId)}&viewer_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      } else {
        setFeedError(true);
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
      setFeedError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchBirthdays = async () => {
    try {
      const churchId = user?.church_id;
      if (!churchId) {
        setBirthdaysThisMonth([]);
        return;
      }
      const response = await fetch(`${API_URL}/api/comms/v3/birthdays/month?church_id=${encodeURIComponent(churchId)}`);
      if (response.ok) {
        const data = await response.json();
        setBirthdaysThisMonth(data.birthdays || []);
      }
    } catch (err) {
      console.error('Error fetching birthdays:', err);
    }
  };

  useEffect(() => {
    // Fetch if no posts, OR if cached posts belong to a different church
    const postsMatchChurch = globalPosts.length > 0 && globalPosts[0]?.church_id === user?.church_id;
    if (!postsMatchChurch && user?.church_id) fetchPosts();
    fetchBirthdays();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, user?.church_id, globalPosts.length]);

  // Show search results when typing
  useEffect(() => {
    if (isSearchActive) {
      setShowSearchResults(true);
    }
  }, [isSearchActive, feedResults, messageResults]);

  // Handle search result click - navigate to the appropriate location
  const handleSearchResultClick = (result) => {
    setShowSearchResults(false);
    setShowMobileSearch(false);
    clearSearch();
    
    if (result.type === 'post') {
      // Navigate to Team Feed and highlight post
      setCurrentView('feed');
      setHighlightedPostId(result.postId);
      setTimeout(() => setHighlightedPostId(null), 3000);
    } else if (result.type === 'comment') {
      // Navigate to Team Feed and highlight post + comment
      setCurrentView('feed');
      setHighlightedPostId(result.postId);
      setHighlightedCommentId(result.commentId);
      setTimeout(() => {
        setHighlightedPostId(null);
        setHighlightedCommentId(null);
      }, 3000);
    } else if (result.type === 'message') {
      // Navigate to Messages view and pass the target message
      setCurrentView('messages');
      setTargetMessageData({
        conversationId: result.conversationId,
        conversation: result.conversation,
        message: result.message,
        searchQuery: searchQuery
      });
      // Clear target after a delay to allow MessagingApp to process it
      setTimeout(() => setTargetMessageData(null), 500);
    }
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // If viewing settings, show the settings page instead of main content
  if (currentView === 'settings' && isTeamMemberView) {
    return (
      <TeamMemberProfileSettings
        user={user}
        onBack={() => setCurrentView('feed')}
        onLogout={onBack}
        onUpdateUser={onUpdateUser}
      />
    );
  }

  return (
    <div data-ui="page" className="min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] overflow-x-hidden">
      {/* Header - with safe area padding for PWA, fixed on all screen sizes */}
      <header className="bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-sm border-b border-slate-200 dark:border-white/10 fixed top-0 left-0 right-0 z-20 pt-[max(10px,env(safe-area-inset-top))]">
        <div className={isStudio ? "st-hdr" : "max-w-6xl mx-auto px-2 md:px-4 py-2"}>
          <div className={isStudio ? "st-hdrrow" : "flex items-center justify-between gap-2"}>
            {/* Left Side - Back button and Title (Admin view) or Logout (Team Member) or empty (Standalone) */}
            {isTeamMemberView ? (
              <div className={isStudio ? "st-hdrleft" : "flex items-center gap-2 flex-shrink-0"}>
                <div className="flex items-center gap-2">
                  <div data-tile="comms" className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md shadow-blue-500/25">
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  <h1 className={isStudio ? "st-apptitle" : "text-base md:text-lg font-semibold text-slate-800 dark:text-slate-100"}>Team Comms</h1>
                </div>
              </div>
            ) : !isStandalone ? (
              <div className={isStudio ? "st-hdrleft" : "flex items-center gap-2 flex-shrink-0"}>
                <Button variant="ghost" size="icon" onClick={onBack} className={isStudio ? 'st-ibtn' : 'h-9 w-9'} data-testid="header-back-btn">
                  <ArrowLeft className={isStudio ? 'w-[18px] h-[18px]' : 'w-5 h-5'} />
                </Button>
                <div className="flex items-center gap-2">
                  <div data-tile="comms" className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md shadow-blue-500/25">
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  <h1 className={isStudio ? "st-apptitle" : "text-base md:text-lg font-semibold text-slate-800 dark:text-slate-100"}>Team Comms</h1>
                </div>
              </div>
            ) : (
              <div className={isStudio ? "st-hdrleft" : "flex-shrink-0"} /> 
            )}

            {/* Watch Instructions Button — admin/owner only, dismissable */}
            {!introVideoDismissed && !isTeamMemberView && (user?.role === 'admin' || user?.role === 'owner') && (
              <button
                data-ui="howto"
                data-testid="team-comms-intro-video-btn"
                onClick={() => setShowIntroVideo(true)}
                className={isStudio
                  ? 'st-btn st-ghost st-sm st-howto hidden md:inline-flex'
                  : 'relative px-3 py-1.5 rounded-lg bg-orange-500 dark:bg-orange-500/90 text-white text-sm font-medium hidden md:flex items-center gap-1.5 overflow-hidden group flex-shrink-0'}
                style={{ isolation: 'isolate' }}
              >
                <span
                  className="absolute inset-0 rounded-lg dark:opacity-80"
                  style={{
                    background: `conic-gradient(from var(--angle), transparent 60%, #faff00 80%, transparent 100%)`,
                    animation: 'spin-snake 3s linear infinite',
                    padding: '2px',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  }}
                />
                <Play className="w-3.5 h-3.5 relative z-10" />
                {/* Full label on lg+, shortened on md to fit between title + search */}
                <span className="relative z-10 hidden lg:inline">{isStudio ? 'How it works' : 'Watch Instructions'}</span>
                <span className="relative z-10 inline lg:hidden">{isStudio ? 'How it works' : 'Instructions'}</span>
              </button>
            )}
            
            {/* Right Side Actions - Search, Profile, Logout */}
            <div className={isStudio ? 'st-contents' : 'flex items-center gap-1'}>
              {/* Search Bar */}
              <div className={isStudio ? 'st-searchwrap hidden md:block' : 'relative flex-1 max-w-md hidden md:block'} ref={searchRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  <Input
                    type="text"
                    placeholder="Search posts, messages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => isSearchActive && setShowSearchResults(true)}
                    className={isStudio ? 'st-search' : 'pl-9 pr-4 h-9 bg-slate-50 dark:bg-[#1e1f22] border-slate-200 dark:border-white/10 focus:bg-white dark:focus:bg-[#2d2f33] text-sm w-64'}
                    data-testid="comms-search-input"
                  />
                  {isStudio && !searchQuery && !isSearching && (
                    <kbd className="st-key" aria-hidden="true">/</kbd>
                  )}
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  {searchQuery && !isSearching && (
                    <button
                      onClick={() => { clearSearch(); setShowSearchResults(false); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Search Results Dropdown - Desktop Only */}
                {showSearchResults && !showMobileSearch && (
                  <SearchResultsDropdown
                    isSearching={isSearching}
                    isSearchActive={isSearchActive}
                    feedResults={feedResults}
                    messageResults={messageResults}
                    searchQuery={searchQuery}
                    onResultClick={handleSearchResultClick}
                    onClose={() => setShowSearchResults(false)}
                  />
                )}
              </div>
              
              {/* Mobile Search Button */}
              <button
                onClick={() => setShowMobileSearch(true)}
                className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors flex items-center justify-center w-9 h-9"
                data-testid="search-btn-mobile"
              >
                <Search className="w-5 h-5 text-slate-600 dark:text-slate-300" />
              </button>
              
              {/* Notification Bell */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors flex items-center justify-center w-9 h-9 bell-hover-wiggle"
                  data-testid="comms-notification-bell"
                >
                  <Bell className={`w-5 h-5 text-slate-600 dark:text-slate-300 bell-hover-target ${bellRinging ? 'bell-ring' : ''}`} />
                  {(unreadCount || 0) > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
                      {(unreadCount || 0) > 9 ? '9+' : (unreadCount || 0)}
                    </span>
                  )}
                </button>
                
                {/* Notification Dropdown - Team Comms Only (excludes onboarding) */}
                {showNotifications && (
                  <NotificationPanel
                    notifications={teamCommsNotifications || []}
                    onboardingNotifications={[]}
                    user={user}
                    excludeOnboarding={true}
                    unreadCount={unreadCount || 0}
                    API_URL={API_URL}
                    activeConversationId={currentView === 'messages' ? activeConversationId : null}
                    onClose={() => setShowNotifications(false)}
                    onMarkAllRead={() => { if (markAllRead) markAllRead(); }}
                    onNotificationClick={async (notification) => {
                      try {
                        // Mark all notifications in this group as read via hook
                        const groupIds = notification._groupIds || [notification.id];
                        if (markReadByIds) markReadByIds(groupIds);
                        
                        // Navigate based on notification type
                        if (notification.type === 'workshop') {
                          // New quarterly team workshop — leave comms, go to the page
                          if (notification.workshop_url) {
                            spaNavigate(notification.workshop_url);
                          } else if (user?.church_slug) {
                            spaNavigate(`/${user.church_slug}/training/workshop`);
                          }
                        } else if (notification.type === 'dm' || notification.type === 'message_reaction') {
                          const conversationId = notification.conversation_id;
                          const messageId = notification.message_id;
                          if (conversationId) {
                            if (markMessagesAsRead && messageId) {
                              markMessagesAsRead(user.id, [messageId], conversationId);
                            }
                            setMessageHighlight({
                              conversationId,
                              messageId: messageId || null
                            });
                            setCurrentView('messages');
                          } else {
                            setCurrentView('messages');
                          }
                        } else if (['service_request','service_response','service_updated','service_comment'].includes(notification.type)) {
                          setCurrentView('services');
                          setHighlightServiceId(notification.service_id || null);
                          setHighlightServiceType(notification.type || null);
                        } else if (notification.type === 'shared_planner') {
                          if (notification.shared_item_type === 'task') {
                            // Task lives in the comms Tasks view — highlight it there
                            setCurrentView('tasks');
                            refetchShared();
                            if (notification.shared_item_id) {
                              setHighlightSharedTaskId(notification.shared_item_id);
                              setTimeout(() => setHighlightSharedTaskId(null), 3500);
                            }
                          } else {
                            // Goals/priorities live in the Planner page
                            const params = new URLSearchParams();
                            if (notification.shared_item_id) params.set('shared_highlight', notification.shared_item_id);
                            params.set('shared_type', notification.shared_item_type || 'goal');
                            spaNavigate(`/planner?${params.toString()}`);
                          }
                        } else if (notification.type === 'post_reaction') {
                          setCurrentView('feed');
                          if (notification.post_id) {
                            setHighlightedPostId(notification.post_id);
                            setTimeout(() => setHighlightedPostId(null), 3000);
                          }
                        } else if (notification.type === 'comment_reaction') {
                          setCurrentView('feed');
                          if (notification.post_id) {
                            setHighlightedPostId(notification.post_id);
                            if (notification.comment_id) {
                              setHighlightedCommentId(notification.comment_id);
                              setTimeout(() => {
                                setHighlightedCommentId(null);
                                setHighlightedPostId(null);
                              }, 3000);
                            } else {
                              setTimeout(() => setHighlightedPostId(null), 3000);
                            }
                          }
                        } else {
                          setCurrentView('feed');
                          if (notification.post_id) {
                            setHighlightedPostId(notification.post_id);
                            if (notification.comment_id) {
                              setHighlightedCommentId(notification.comment_id);
                              setTimeout(() => {
                                setHighlightedCommentId(null);
                                setHighlightedPostId(null);
                              }, 3000);
                            } else {
                              setTimeout(() => setHighlightedPostId(null), 3000);
                            }
                          }
                        }
                      } catch (err) {
                        console.error('Error handling notification click:', err);
                      }
                    }}
                  />
                )}
              </div>
              
              {/* Profile Avatar Dropdown - For team member view */}
              {(isTeamMemberView || isStudio) && (
                <div className="relative" ref={profileDropdownRef}>
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="flex items-center gap-1 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                    data-testid="profile-avatar-btn"
                  >
                    <UserAvatar 
                      name={user?.name || user?.user_metadata?.name || user?.email}
                      avatarUrl={user?.avatar}
                      size="sm"
                      showBorder
                      showShadow
                    />
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                  </button>
                  
                  {/* Profile Dropdown Menu */}
                  {showProfileDropdown && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-card rounded-xl shadow-xl border border-slate-200 dark:border-white/10 z-50 overflow-hidden">
                      {/* User Info Header */}
                      <div className="p-3 border-b border-slate-100 dark:border-white/10 bg-slate-50 dark:bg-[#1e1f22]">
                        <p className="font-medium text-sm text-slate-800 dark:text-slate-100 truncate">
                          {user?.name || user?.user_metadata?.name || 'Team Member'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                      </div>
                      
                      {/* Menu Items */}
                      <div className="p-1">
                        <button
                          onClick={() => {
                            setShowProfileDropdown(false);
                            setCurrentView('settings');
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                          data-testid="settings-menu-item"
                        >
                          <Settings className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                          {isStudio ? 'Profile settings' : 'Profile Settings'}
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileDropdown(false);
                            onBack();
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/15 rounded-lg transition-colors"
                          data-testid="logout-menu-item"
                        >
                          <LogOut className="w-4 h-4" />
                          {isStudio ? 'Log out' : 'Log Out'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Simple Logout Button - For standalone (admin) view only */}
              {isStandalone && !isTeamMemberView && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={onBack} 
                  className="h-9 w-9" 
                  title="Log out"
                  data-testid="header-logout-btn"
                >
                  <LogOut className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                </Button>
              )}
            </div>
            
            {!isStudio && (
              <div className="text-[10px] md:text-xs text-slate-400 dark:text-slate-500 hidden lg:block flex-shrink-0">
                Content auto-deletes after 90 days
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Search Overlay */}
      {showMobileSearch && (
        <div className="md:hidden fixed inset-0 z-50 bg-card" ref={mobileSearchRef}>
          <div className="flex items-center gap-2 p-3 border-b border-slate-200 dark:border-white/10 pt-[max(12px,env(safe-area-inset-top))]">
            <button
              onClick={() => {
                setShowMobileSearch(false);
                clearSearch();
              }}
              className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </button>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <Input
                type="text"
                placeholder="Search posts, messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-10 h-10 bg-slate-50 dark:bg-[#1e1f22] border-slate-200 dark:border-white/10 focus:bg-white dark:focus:bg-[#2d2f33]"
                autoFocus
                data-testid="mobile-search-input"
              />
              {searchQuery && !isSearching && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {isSearching && (
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          
          {/* Mobile Search Results */}
          <div className="overflow-y-auto max-h-[calc(100vh-60px)]">
            {(feedResults.length > 0 || messageResults.length > 0) ? (
              <div>
                {/* Feed Results Section */}
                {feedResults.length > 0 && (
                  <div>
                    <div className="sticky top-0 bg-slate-50 dark:bg-[#1e1f22] px-4 py-2 border-b border-slate-200 dark:border-white/10">
                      <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Team Feed ({feedResults.length})
                      </h3>
                    </div>
                    {feedResults.map((result) => (
                      <button
                        key={`mobile-feed-${result.type}-${result.id}`}
                        onClick={() => handleSearchResultClick(result)}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors border-b border-slate-100 dark:border-white/10"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                            result.type === 'post' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                          }`}>
                            {result.type === 'post' ? 'Post' : 'Comment'}
                          </span>
                          <span className="text-xs text-slate-400 dark:text-slate-500">{result.authorName}</span>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-200 line-clamp-2">{result.snippet || '(No text content)'}</p>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Messages Results Section */}
                {messageResults.length > 0 && (
                  <div>
                    <div className="sticky top-0 bg-slate-50 dark:bg-[#1e1f22] px-4 py-2 border-b border-slate-200 dark:border-white/10">
                      <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Messages ({messageResults.length})
                      </h3>
                    </div>
                    {messageResults.map((result) => (
                      <button
                        key={`mobile-msg-${result.id}`}
                        onClick={() => handleSearchResultClick(result)}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors border-b border-slate-100 dark:border-white/10"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300">
                            Message
                          </span>
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            {result.authorName} • {result.conversationName}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-200 line-clamp-2">{result.snippet || '(No text content)'}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : isSearchActive && !isSearching ? (
              <div className="p-8 text-center">
                <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">No results for &quot;{searchQuery}&quot;</p>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">Type at least 2 characters to search</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={isStudio ? `st-comms${currentView === 'feed' ? '' : ' st-comms-wide'}` : "max-w-6xl mx-auto flex pt-[calc(52px+max(10px,env(safe-area-inset-top)))] md:pt-[57px] md:pl-52"}>
        {/* Left Sidebar with Tabs - Desktop Only, Fixed */}
        <aside data-ui="rail" className={isStudio ? "st-rail" : "hidden md:flex w-48 flex-shrink-0 flex-col gap-2 p-4 border-r border-slate-200 dark:border-white/10 bg-card fixed top-[57px] left-0 bottom-0 z-10 md:left-[max(0px,calc((100vw-1152px)/2))]"}>
          <button
            onClick={() => {
              setCurrentView('feed');
              setActiveConversationId(null); // Clear active conversation when leaving messages
            }}
            className={isStudio
              ? `st-railbtn${currentView === 'feed' ? ' st-on' : ''}`
              : `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-left
              ${currentView === 'feed' 
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25' 
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
              }
            `}
            data-active={currentView === 'feed' ? 'true' : undefined}
            data-testid="sidebar-team-feed"
          >
            <Users className="w-5 h-5 flex-shrink-0" />
            <span className="whitespace-nowrap">{isStudio ? 'Feed' : 'Team Feed'}</span>
            {hasUnreadFeed && currentView !== 'feed' && (
              <span className={isStudio ? 'st-raildot' : "w-2 h-2 rounded-full bg-red-500 ml-auto flex-shrink-0"} data-testid="feed-unread-dot" />
            )}
          </button>
          
          <button
            onClick={() => setCurrentView('messages')}
            className={isStudio
              ? `st-railbtn${currentView === 'messages' ? ' st-on' : ''}`
              : `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-left
              ${currentView === 'messages' 
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25' 
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
              }
            `}
            data-active={currentView === 'messages' ? 'true' : undefined}
            data-testid="sidebar-messages"
          >
            <MessageSquare className="w-5 h-5" />
            <span>Messages</span>
            {totalUnreadDMs > 0 && currentView !== 'messages' && (
              <span className={isStudio ? 'st-raildot' : "w-2 h-2 rounded-full bg-red-500 ml-auto flex-shrink-0"} data-testid="messages-unread-dot" />
            )}
          </button>

          {/* Services - Simple Planner churches: people on a service, and admins (Fix 7 G3) */}
          {canSeeServicesTab && (
            <button
              onClick={() => { setCurrentView('services'); setActiveConversationId(null); }}
              className={isStudio
                ? `st-railbtn${currentView === 'services' ? ' st-on' : ''}`
                : `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-left
                ${currentView === 'services' 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25' 
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
                }
              `}
              data-active={currentView === 'services' ? 'true' : undefined}
              data-testid="sidebar-services"
            >
              <Music className="w-5 h-5" />
              <span>Services</span>
              {servicesPendingCount > 0 && currentView !== 'services' && (
                <span className={isStudio ? 'st-raildot' : "w-2 h-2 rounded-full bg-red-500 ml-auto flex-shrink-0"} data-testid="services-unread-dot" />
              )}
            </button>
          )}
          
          {/* Groups - Admin/Owner only */}
          {(user?.role === 'admin' || user?.role === 'owner') && (
            <button
              onClick={() => { setCurrentView('groups'); setActiveConversationId(null); }}
              className={isStudio
              ? `st-railbtn${currentView === 'groups' ? ' st-on' : ''}`
              : `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-left
              ${currentView === 'groups' 
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25' 
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
              }
            `}
              data-active={currentView === 'groups' ? 'true' : undefined}
            data-testid="sidebar-groups"
            >
              <Tag className="w-5 h-5" />
              <span>Groups</span>
            </button>
          )}
        </aside>

        {/* Main Content Area */}
        <main className={isStudio ? "st-contents" : "flex-1 min-h-[calc(100vh-57px)] pb-16 md:pb-0 overflow-x-hidden max-w-full"}>
          {currentView === 'feed' && (
            <div className={isStudio ? "st-contents" : "flex gap-6 max-w-5xl mx-auto"}>
              {/* Main Feed */}
              <div className={isStudio ? "st-contents" : "flex-1 min-w-0"}>
                <FeedView 
                  user={user} 
                  posts={posts} 
                  setPosts={setPosts}
                  onRefresh={fetchPosts}
                  leadersOnlyPosting={leadersOnlyPosting}
                  feedError={feedError}
                  highlightedPostId={highlightedPostId}
                  highlightedCommentId={highlightedCommentId}
                  isOnline={isOnline}
                  unreadPostIds={new Set(
                    (teamCommsNotifications || [])
                      .filter(n => !n.is_read && n.sender_id !== user?.id && n.post_id && ['post', 'comment', 'reply', 'post_reaction', 'comment_reaction'].includes(n.type))
                      .map(n => n.post_id)
                  )}
                  onPostSeen={(postId) => {
                    if (markReadByTarget) markReadByTarget({ postId });
                  }}
                />
              </div>
              
              {/* Birthday Sidebar - Hidden on mobile, aligned with feed content */}
              <div className={isStudio ? "st-side" : "hidden lg:block w-64 flex-shrink-0 py-6 pr-4"}>
                <Card className="bg-card border border-slate-200 dark:border-white/10 shadow-sm">
                  <CardHeader className="pb-2 pt-4">
                    <div className="flex items-center gap-3">
                      <div data-ui="side-icon" className="w-9 h-9 rounded-full bg-rose-50 dark:bg-rose-950/40 flex items-center justify-center">
                        <Cake className="w-4 h-4 text-rose-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                          Birthdays
                        </CardTitle>
                        <p className="text-xs text-slate-400 dark:text-slate-500">
                          {isStudio ? new Date().toLocaleDateString('en-US', { month: 'long' }) : 'This month'}
                        </p>
                      </div>
                      {isChurchAdmin && (
                        <Popover onOpenChange={(open) => { if (open) fetchBirthdayPostsSetting(); }}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                              aria-label="Birthday reminder settings"
                              data-testid="birthday-settings-btn"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-80 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Birthday reminders</p>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                                  Automatically post a birthday celebration to the team feed on each member's birthday.
                                </p>
                              </div>
                              <Switch
                                checked={birthdayPosts.enabled}
                                onCheckedChange={(enabled) => saveBirthdayPosts({ enabled })}
                                data-testid="birthday-posts-toggle"
                              />
                            </div>
                            <div className={`mt-4 pt-3 border-t border-slate-200 dark:border-white/10 ${birthdayPosts.enabled ? '' : 'opacity-50'}`}>
                              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Who sees the post</p>
                              <RadioGroup
                                value={birthdayPosts.audience}
                                onValueChange={(audience) => saveBirthdayPosts({ audience })}
                                disabled={!birthdayPosts.enabled}
                                aria-label="Birthday post audience"
                              >
                                {[
                                  { value: 'everyone', label: 'Everyone on the team', hint: 'The whole church sees it and gets notified.' },
                                  { value: 'groups', label: 'Only their groups', hint: "Just the groups they're in. Members of no groups still post to everyone." },
                                ].map(({ value, label, hint }) => (
                                  <label key={value} htmlFor={`birthday-audience-${value}`} className="flex items-start gap-2.5 cursor-pointer">
                                    <RadioGroupItem value={value} id={`birthday-audience-${value}`} className="mt-0.5" data-testid={`birthday-audience-${value}`} />
                                    <span>
                                      <span className="block text-sm text-slate-700 dark:text-slate-200">{label}</span>
                                      <span className="block text-xs text-slate-400 dark:text-slate-500">{hint}</span>
                                    </span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-2 pb-4">
                    {birthdaysThisMonth.length === 0 ? (
                      <div className={isStudio ? 'st-bdayempty' : "text-center py-8 px-4"}>
                        <div className={isStudio
                          ? 'w-11 h-11 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3'
                          : 'w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-50 to-pink-50 flex items-center justify-center mx-auto mb-3 border-2 border-dashed border-rose-200 dark:border-rose-900'}>
                          <Cake className={isStudio ? 'w-5 h-5 text-muted-foreground' : 'w-6 h-6 text-rose-300'} />
                        </div>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No birthdays</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Check back next month!</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {birthdaysThisMonth.map((person, index) => (
                          <div 
                            key={index} 
                            className={isStudio ? 'st-bday' : 'flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors'}
                          >
                            <UserAvatar 
                              userId={person.user_id}
                              name={person.name}
                              avatarUrl={person.avatar_url}
                              size="sm"
                              className={isStudio ? 'w-7 h-7 text-[11px]' : 'w-9 h-9'}
                            />
                            <div className={isStudio ? 'st-contents' : 'flex-1 min-w-0'}>
                              <div className={isStudio ? 'st-bdayname' : 'font-medium text-sm text-slate-700 dark:text-slate-200 truncate'}>
                                {isStudio ? (person.name || 'Unknown') : (() => {
                                  const parts = (person.name || 'Unknown').split(' ');
                                  if (parts.length >= 2) {
                                    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
                                  }
                                  return parts[0];
                                })()}
                              </div>
                            </div>
                            <div className={isStudio ? 'st-bdaydate' : 'text-xs font-medium text-rose-500'}>
                              {isStudio
                                ? new Date(new Date().getFullYear(), new Date().getMonth(), person.day)
                                    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                                : getDayWithSuffix(person.day)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                {isStudio && (
                  <p className="st-note90">
                    Posts and messages are kept for 90 days, then removed automatically.
                  </p>
                )}
              </div>
            </div>
          )}

          {currentView === 'messages' && (
            /* Messages View - Renders the full MessagingApp */
            /* On mobile, reduce height to account for bottom tab bar (64px) */
            <div className={isStudio ? "st-msgwrap" : "h-[calc(100vh-57px-64px)] md:h-[calc(100vh-57px)] w-full max-w-full overflow-hidden"}>
              <MessagingApp 
                user={user} 
                onBack={() => setCurrentView('feed')}
                isEmbedded={true}
                initialConversation={targetMessageData?.conversation}
                initialTargetMessage={targetMessageData?.message}
                initialSearchQuery={targetMessageData?.searchQuery}
                messageHighlight={messageHighlight}
                onMessageHighlightComplete={handleMessageHighlightComplete}
                onActiveConversationChange={setActiveConversationId}
                onHighlightConsumed={handleHighlightConsumed}
              />
            </div>
          )}

          {currentView === 'tasks' && canSeeTasksTab && (
            /* Tasks View - Today's tasks (admins + users with shared tasks) */
            <div className={isStudio ? "st-taskwrap" : "h-[calc(100vh-57px-64px)] md:h-[calc(100vh-57px)] w-full max-w-full overflow-y-auto bg-slate-50 dark:bg-[#1e1f22]"}>
              <MobileTasksView
                user={user}
                globalTasks={globalTasks}
                setGlobalTasks={setGlobalTasks}
                sharedTasks={mySharedTasks}
                sharedContext={sharedContext}
                onToggleSharedTask={toggleSharedItem}
                onDeleteSharedTask={deleteSharedItem}
                onUpdateSharedTask={updateSharedItem}
                onCreateSharedTask={createSharedItem}
                highlightSharedTaskId={highlightSharedTaskId}
                setHoveredItem={setHoveredItem}
              />
            </div>
          )}

          {currentView === 'services' && (
            <ServicesView user={user} highlightServiceId={highlightServiceId} highlightServiceType={highlightServiceType} highlightNonce={highlightServiceNonce} onHighlightConsumed={clearServiceHighlight} notifStamp={servicesNotifStamp} onPendingChange={setServicesPendingCount} />
          )}

          {currentView === 'groups' && (user?.role === 'admin' || user?.role === 'owner') && (
            <GroupsView user={user} />
          )}
        </main>
      </div>

      {/* Mobile Bottom Tabs - Only visible on mobile, hidden when in settings */}
      {currentView !== 'settings' && (
        <div data-ui="comms-tabs" className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-slate-200 dark:border-white/10 z-30 pb-[env(safe-area-inset-bottom)]">
          <div className={isStudio ? "flex st-tabrow" : "flex"}>
            <button
              onClick={() => {
                setCurrentView('feed');
                setActiveConversationId(null); // Clear active conversation when leaving messages
              }}
              className={isStudio
                ? `st-tabbtn${currentView === 'feed' ? ' st-on' : ''}`
                : `flex-1 flex flex-row items-center justify-center py-2 gap-1.5 transition-colors
                ${currentView === 'feed' 
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' 
                  : 'text-slate-500 dark:text-slate-400'
                }
              `}
              data-testid="mobile-tab-feed"
            >
              <Users className={isStudio ? undefined : `w-4 h-4 ${currentView === 'feed' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500'}`} />
              <div className={isStudio ? "st-contents" : "relative"}>
                <span className={isStudio ? undefined : "text-xs font-medium"}>Feed</span>
                {hasUnreadFeed && currentView !== 'feed' && (
                  <span className={isStudio ? 'st-tabdot' : "absolute -top-1 -right-2 w-2 h-2 rounded-full bg-red-500"} data-testid="mobile-feed-unread-dot" />
                )}
              </div>
            </button>
            
            <button
              onClick={() => setCurrentView('messages')}
              className={isStudio
                ? `st-tabbtn${currentView === 'messages' ? ' st-on' : ''}`
                : `flex-1 flex flex-row items-center justify-center py-2 gap-1.5 transition-colors
                ${currentView === 'messages' 
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' 
                  : 'text-slate-500 dark:text-slate-400'
                }
              `}
              data-testid="mobile-tab-messages"
            >
              <MessageSquare className={isStudio ? undefined : `w-4 h-4 ${currentView === 'messages' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500'}`} />
              <div className={isStudio ? "st-contents" : "relative"}>
                <span className={isStudio ? undefined : "text-xs font-medium"}>Messages</span>
                {totalUnreadDMs > 0 && currentView !== 'messages' && (
                  <span className={isStudio ? 'st-tabdot' : "absolute -top-1 -right-2 w-2 h-2 rounded-full bg-red-500"} data-testid="mobile-messages-unread-dot" />
                )}
              </div>
            </button>

            {/* Tasks tab - admins + users with shared tasks */}
            {canSeeTasksTab && (
              <button
                onClick={() => {
                  setCurrentView('tasks');
                  setActiveConversationId(null); // Clear active conversation when leaving messages
                }}
                className={isStudio
                  ? `st-tabbtn${currentView === 'tasks' ? ' st-on' : ''}`
                  : `flex-1 flex flex-row items-center justify-center py-2 gap-1.5 transition-colors
                  ${currentView === 'tasks' 
                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' 
                    : 'text-slate-500 dark:text-slate-400'
                  }
                `}
                data-testid="mobile-tab-tasks"
              >
                <CheckSquare className={isStudio ? undefined : `w-4 h-4 ${currentView === 'tasks' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500'}`} />
                <span className={isStudio ? undefined : "text-xs font-medium"}>Tasks</span>
              </button>
            )}

            {/* Services tab - Simple Planner churches: people on a service, and admins */}
            {canSeeServicesTab && (
              <button
                onClick={() => {
                  setCurrentView('services');
                  setActiveConversationId(null); // Clear active conversation when leaving messages
                }}
                className={isStudio
                  ? `st-tabbtn${currentView === 'services' ? ' st-on' : ''}`
                  : `flex-1 flex flex-row items-center justify-center py-2 gap-1.5 transition-colors
                  ${currentView === 'services' 
                    ? 'text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/40' 
                    : 'text-slate-500 dark:text-slate-400'
                  }
                `}
                data-testid="mobile-tab-services"
              >
                <Music className={isStudio ? undefined : `w-4 h-4 ${currentView === 'services' ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400 dark:text-slate-500'}`} />
                <div className={isStudio ? "st-contents" : "relative"}>
                  <span className={isStudio ? undefined : "text-xs font-medium"}>Services</span>
                  {servicesPendingCount > 0 && currentView !== 'services' && (
                    <span className={isStudio ? 'st-tabdot' : "absolute -top-1 -right-2 w-2 h-2 rounded-full bg-red-500"} data-testid="mobile-services-unread-dot" />
                  )}
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Team Comms Intro Video Modal */}
      {showIntroVideo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={handleDismissIntro}>
          <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handleDismissIntro}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
              data-testid="team-comms-intro-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div
              style={{
                position: 'relative',
                width: '100%',
                paddingBottom: '56.25%',
                backgroundColor: '#000'
              }}
            >
              <iframe
                src="https://www.loom.com/embed/18f8bf3025ba4daba4f3f18b0803d496?autoplay=1"
                frameBorder="0"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title="Team Comms Instructions"
              />
            </div>
            <div className="px-4 py-2 bg-black/40 text-right">
              <a
                href="https://www.loom.com/share/18f8bf3025ba4daba4f3f18b0803d496"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-white/70 hover:text-white"
                data-testid="team-comms-intro-fallback"
              >
                Trouble playing? Open in Loom ↗
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default TeamCommsApp;
